/**
 * Tasks Page
 * Kanban board and list view for task management
 */

import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, LayoutGrid, List } from 'lucide-react';
import { KanbanBoard } from '../components/KanbanBoard';
import { TaskListView } from '../components/TaskListView';
import { TaskFilters } from '../components/TaskFilters';
import { TaskFormModal } from '../components/TaskFormModal';
import { useTasksStats, useQuoteOptions, useAssigneeOptions } from '../hooks/useTasks';
import { useOperationsPermissions } from '@/features/operations-settings';
import type { KanbanQueryParams } from '../types';

type ViewMode = 'kanban' | 'list';

const FILTERS_STORAGE_KEY = 'atek-tasks-filters';

// Filters persisted across reloads. `search` is intentionally excluded — search
// text feels ephemeral; users don't expect their last query to come back.
function loadPersistedFilters(): KanbanQueryParams {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    delete parsed.projectId;
    return parsed as KanbanQueryParams;
  } catch {
    return {};
  }
}

function persistFilters(filters: KanbanQueryParams) {
  try {
    const persistable: KanbanQueryParams = { ...filters };
    delete persistable.search;
    delete persistable.projectId;
    if (Object.keys(persistable).length === 0) {
      localStorage.removeItem(FILTERS_STORAGE_KEY);
    } else {
      localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(persistable));
    }
  } catch {
    // localStorage may be unavailable (private mode, quota); ignore.
  }
}

export function TasksPage() {
  const [viewMode, setViewMode] = useState<ViewMode>('kanban');
  const [filters, setFilters] = useState<KanbanQueryParams>(() => loadPersistedFilters());
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    persistFilters(filters);
  }, [filters]);

  const { data: stats } = useTasksStats();
  const { data: quotes = [] } = useQuoteOptions();
  const { data: assignees = [] } = useAssigneeOptions();
  const { canCreateTasks, canEditTasks, canDeleteTasks, canAssignTasks } = useOperationsPermissions();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Tasks</h1>
          <p className="text-sm text-muted-foreground">
            {stats
              ? `${stats.total_count} total tasks, ${stats.by_status?.in_progress ?? 0} in progress`
              : 'Manage your tasks'}
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* View Toggle */}
          <div className="flex items-center border rounded-md">
            <Button
              variant={viewMode === 'kanban' ? 'secondary' : 'ghost'}
              size="sm"
              className="rounded-r-none"
              onClick={() => setViewMode('kanban')}
              aria-label="Kanban view"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="sm"
              className="rounded-l-none"
              onClick={() => setViewMode('list')}
              aria-label="List view"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>

          {canCreateTasks && (
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Task
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <TaskFilters
        filters={filters}
        onFiltersChange={setFilters}
        quotes={quotes}
        assignees={assignees}
      />

      {/* View Content */}
      {viewMode === 'kanban' ? (
        <KanbanBoard
          filters={filters}
          quotes={quotes}
          assignees={assignees}
          canEditTasks={canEditTasks}
          canDeleteTasks={canDeleteTasks}
          canAssignTasks={canAssignTasks}
        />
      ) : (
        <TaskListView
          filters={filters}
          quotes={quotes}
          assignees={assignees}
          canEditTasks={canEditTasks}
          canDeleteTasks={canDeleteTasks}
          canAssignTasks={canAssignTasks}
        />
      )}

      {/* Create Task Modal */}
      <TaskFormModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        quotes={quotes}
        assignees={assignees}
        canAssignTasks={canAssignTasks}
      />
    </div>
  );
}
